import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, ArrowRight, ChevronLeft, ChevronRight, CheckCircle,
  Brain, Users, Calendar, TrendingUp, Stethoscope, Smile, HeartPulse,
  Building2, Activity, UserPlus, Play,
} from 'lucide-react';

const REGISTER_URL = '/register?vertical=salud';
const regUrl = (area) => (area ? `/register?vertical=salud&area=${area}` : REGISTER_URL);

const IMG = {
  virtual: 'https://images.unsplash.com/photo-1758691462743-f9fc9e430d39?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  medicina: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  dental: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  psico: 'https://images.unsplash.com/photo-1714976694525-71eb29a7c500?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  nurse: 'https://images.unsplash.com/photo-1622253694238-3b22139576c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  clinic: 'https://images.unsplash.com/photo-1586773860418-d37222d8fce3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
};

const SLIDES = [
  {
    id: 'virtual',
    eyebrow: 'Consultorio virtual con IA',
    icon: Brain,
    title: 'Tu consultorio virtual inteligente con IA',
    highlight: 'inteligente con IA',
    subtitle: 'Gestiona pacientes, agenda, documentos, comunicación y crecimiento profesional desde un solo sistema.',
    image: IMG.virtual,
    imageAlt: 'Profesional de la salud en teleconsulta',
    stats: [
      { icon: Calendar, k: 'Citas hoy', v: '12' },
      { icon: UserPlus, k: 'Pacientes', v: '340' },
      { icon: Activity, k: 'Seguimientos', v: '9' },
    ],
    primary: { label: 'Crear mi espacio profesional', to: REGISTER_URL },
    secondary: { label: 'Probar gratis', to: REGISTER_URL },
  },
  {
    id: 'medicina',
    eyebrow: 'Medicina',
    icon: Stethoscope,
    title: 'Potencia tu práctica médica',
    highlight: 'práctica médica',
    subtitle: 'Consultorio moderno y tecnología médica al servicio de tus pacientes, en una sola plataforma.',
    image: IMG.medicina,
    imageAlt: 'Médico profesional en consultorio moderno',
    benefits: ['Agenda inteligente', 'Historia clínica digital', 'Seguimiento de pacientes', 'Teleconsulta', 'Documentos profesionales'],
    primary: { label: 'Crear espacio médico', to: regUrl('medicina') },
  },
  {
    id: 'odontologia',
    eyebrow: 'Odontología',
    icon: Smile,
    title: 'La tecnología para transformar tu clínica dental',
    highlight: 'clínica dental',
    subtitle: 'Odontólogos y clínicas dentales modernas con gestión profesional de principio a fin.',
    image: IMG.dental,
    imageAlt: 'Clínica dental moderna',
    benefits: ['Agenda dental', 'Pacientes', 'Tratamientos', 'Seguimiento clínico', 'Gestión profesional'],
    primary: { label: 'Crear espacio odontológico', to: regUrl('odontologia') },
  },
  {
    id: 'psicologia',
    eyebrow: 'Psicología y terapias',
    icon: Brain,
    title: 'Un espacio digital para acompañar a tus pacientes',
    highlight: 'acompañar a tus pacientes',
    subtitle: 'Psicólogos y terapeutas con un entorno privado, seguro y organizado para cada sesión.',
    image: IMG.psico,
    imageAlt: 'Sesión de terapia psicológica',
    benefits: ['Gestión de sesiones', 'Seguimientos', 'Agenda', 'Comunicación segura', 'Organización profesional'],
    primary: { label: 'Crear espacio psicológico', to: regUrl('psicologia') },
  },
  {
    id: 'enfermeria',
    eyebrow: 'Enfermería y especialistas',
    icon: HeartPulse,
    title: 'Organiza tu atención profesional',
    highlight: 'atención profesional',
    subtitle: 'Enfermería y equipos sanitarios con todo lo necesario para una atención clínica ordenada.',
    image: IMG.nurse,
    imageAlt: 'Profesional de enfermería',
    benefits: ['Gestión de pacientes', 'Seguimientos', 'Documentación', 'Organización diaria'],
    primary: { label: 'Crear mi espacio', to: regUrl('enfermeria') },
  },
  {
    id: 'clinicas',
    eyebrow: 'Clínicas y centros de salud',
    icon: Building2,
    title: 'Una plataforma para toda tu organización',
    highlight: 'toda tu organización',
    subtitle: 'Clínicas, hospitales y equipos médicos con administración centralizada y multiusuario.',
    image: IMG.clinic,
    imageAlt: 'Clínica moderna y equipo médico',
    benefits: ['Multiusuario', 'Equipos profesionales', 'Gestión centralizada', 'Administración empresarial'],
    primary: { label: 'Solicitar demostración', to: '#consulta', anchor: true },
  },
];

const FLOATING_CARDS = [
  { icon: Brain, title: 'IA Salud', desc: 'Asistente inteligente para organizar tu práctica.', color: '#14b8a6' },
  { icon: Users, title: 'Pacientes', desc: 'Toda la información en un solo lugar.', color: '#06b6d4' },
  { icon: Calendar, title: 'Agenda', desc: 'Menos espacios vacíos, más productividad.', color: '#3b82f6' },
  { icon: TrendingUp, title: 'Crecimiento', desc: 'Construye tu presencia profesional.', color: '#10b981' },
];

const AUTO_MS = 6500;

function CtaButton({ cta, primary }) {
  const cls = primary
    ? 'relative inline-flex items-center px-7 py-3.5 rounded-2xl bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] text-white font-bold overflow-hidden group shadow-[0_0_40px_rgba(20,184,166,0.45)]'
    : 'inline-flex items-center px-6 py-3.5 rounded-2xl border border-white/20 text-white/90 font-semibold hover:bg-white/10 transition-all';
  const inner = (
    <>
      {primary && <span className="absolute top-0 left-[-60%] h-full w-1/3 bg-white/40 skew-x-[-20deg] blur-md group-hover:left-[140%] transition-all duration-700 ease-out pointer-events-none" />}
      <span className="relative flex items-center">
        {!primary && <Play className="w-4 h-4 mr-2" />}
        {cta.label}
        {primary && <ArrowRight className="w-5 h-5 ml-2" />}
      </span>
    </>
  );
  if (cta.anchor) {
    return <a href={cta.to} data-testid={`hero-cta-${primary ? 'primary' : 'secondary'}`} className={cls}>{inner}</a>;
  }
  return <Link to={cta.to} data-testid={`hero-cta-${primary ? 'primary' : 'secondary'}`} className={cls}>{inner}</Link>;
}

export default function MedicinaHeroSlider() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const go = useCallback((dir) => {
    setIndex((i) => (i + dir + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setTimeout(() => setIndex((i) => (i + 1) % SLIDES.length), AUTO_MS);
    return () => clearTimeout(timer.current);
  }, [index, paused]);

  const slide = SLIDES[index];
  const SlideIcon = slide.icon;

  return (
    <section
      id="hero-slider"
      data-testid="medicina-hero-slider"
      className="relative pt-28 pb-16 md:pt-32 md:pb-24 px-6 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0b1220] via-[#0f172a] to-[#0b1220]" />
      <motion.div aria-hidden="true" animate={{ y: [0, -25, 0], opacity: [0.35, 0.6, 0.35] }} transition={{ repeat: Infinity, duration: 9, ease: 'easeInOut' }} className="absolute -top-10 right-0 w-[28rem] h-[28rem] bg-[#14b8a6]/20 rounded-full blur-3xl pointer-events-none" />
      <motion.div aria-hidden="true" animate={{ y: [0, 25, 0], opacity: [0.3, 0.55, 0.3] }} transition={{ repeat: Infinity, duration: 11, ease: 'easeInOut' }} className="absolute bottom-0 left-0 w-[26rem] h-[26rem] bg-[#3b82f6]/15 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center min-h-[30rem]">
          {/* ==== Left: text ==== */}
          <div>
            <AnimatePresence mode="wait">
              <motion.div
                key={slide.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-[#14b8a6] text-xs font-bold uppercase tracking-[0.25em] mb-6">
                  <SlideIcon className="w-4 h-4" /> {slide.eyebrow}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-[3.4rem] font-extrabold leading-[1.06] tracking-tight mb-5">
                  {slide.title.split(slide.highlight)[0]}
                  <span className="bg-gradient-to-r from-[#14b8a6] via-[#06b6d4] to-[#10b981] bg-clip-text text-transparent">{slide.highlight}</span>
                  {slide.title.split(slide.highlight)[1]}
                </h1>
                <p className="text-lg text-white/75 leading-relaxed mb-7 max-w-xl font-light">{slide.subtitle}</p>

                {slide.benefits && (
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 mb-8 max-w-xl">
                    {slide.benefits.map((b) => (
                      <li key={b} className="flex items-center gap-2.5 text-white/85 text-[15px]">
                        <CheckCircle className="w-5 h-5 text-[#14b8a6] shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex flex-wrap items-center gap-4">
                  <CtaButton cta={slide.primary} primary />
                  {slide.secondary && <CtaButton cta={slide.secondary} />}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Controls */}
            <div className="flex items-center gap-4 mt-10">
              <button aria-label="Anterior" data-testid="hero-prev" onClick={() => go(-1)} className="w-11 h-11 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-white transition-all">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button aria-label="Siguiente" data-testid="hero-next" onClick={() => go(1)} className="w-11 h-11 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-white transition-all">
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 ml-2">
                {SLIDES.map((s, i) => (
                  <button
                    key={s.id}
                    aria-label={`Ir a ${s.eyebrow}`}
                    data-testid={`hero-dot-${i}`}
                    onClick={() => setIndex(i)}
                    className={`h-2 rounded-full transition-all duration-300 ${i === index ? 'w-8 bg-gradient-to-r from-[#14b8a6] to-[#06b6d4]' : 'w-2 bg-white/25 hover:bg-white/40'}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* ==== Right: visual ==== */}
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={slide.id}
                initial={{ opacity: 0, scale: 0.96, x: 20 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.98, x: -20 }}
                transition={{ duration: 0.55, ease: 'easeOut' }}
                className="relative rounded-3xl border border-white/10 overflow-hidden shadow-[0_25px_80px_-20px_rgba(0,0,0,0.75)]"
              >
                <img src={slide.image} alt={slide.imageAlt} className="w-full h-[22rem] md:h-[26rem] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/35 to-transparent" />

                {slide.stats && (
                  <div className="absolute bottom-0 left-0 right-0 p-5 grid grid-cols-3 gap-3">
                    {slide.stats.map((s) => (
                      <div key={s.k} className="rounded-2xl bg-white/[0.09] backdrop-blur-xl border border-white/15 p-3">
                        <s.icon className="w-5 h-5 mb-1 text-[#14b8a6]" />
                        <div className="text-xl font-black text-white leading-none">{s.v}</div>
                        <div className="text-white/60 text-[11px] mt-1">{s.k}</div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Floating glass card top-left */}
            <motion.div
              aria-hidden="true"
              animate={{ y: [0, -12, 0] }}
              transition={{ repeat: Infinity, duration: 5, ease: 'easeInOut' }}
              className="hidden md:flex absolute -top-6 -left-6 items-center gap-3 rounded-2xl bg-white/[0.08] backdrop-blur-xl border border-white/15 px-4 py-3 shadow-2xl"
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#14b8a61f', border: '1px solid #14b8a655' }}>
                <Brain className="w-5 h-5 text-[#14b8a6]" />
              </div>
              <div>
                <div className="text-white text-sm font-bold leading-none">IA Salud</div>
                <div className="text-white/60 text-[11px] mt-1">Organiza tu práctica</div>
              </div>
            </motion.div>

            {/* Floating glass card bottom-right */}
            <motion.div
              aria-hidden="true"
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
              className="hidden md:flex absolute -bottom-6 -right-4 items-center gap-3 rounded-2xl bg-white/[0.08] backdrop-blur-xl border border-white/15 px-4 py-3 shadow-2xl"
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#10b9811f', border: '1px solid #10b98155' }}>
                <TrendingUp className="w-5 h-5 text-[#10b981]" />
              </div>
              <div>
                <div className="text-white text-sm font-bold leading-none">Crecimiento</div>
                <div className="text-white/60 text-[11px] mt-1">Presencia profesional</div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ==== Feature cards row ==== */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
          {FLOATING_CARDS.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              whileHover={{ y: -6 }}
              className="group rounded-2xl border border-white/10 backdrop-blur-xl bg-white/[0.04] p-5 hover:border-white/25 hover:bg-white/[0.07] transition-all"
            >
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ background: `${c.color}1f`, border: `1px solid ${c.color}55` }}>
                <c.icon className="w-6 h-6" style={{ color: c.color }} />
              </div>
              <div className="text-white font-bold mb-1">{c.title}</div>
              <div className="text-white/60 text-sm leading-relaxed">{c.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
